import { z } from 'zod';
import { CPFSchema, CNPJSchema, OptionalCNPJSchema, EmailSchema, PhoneBRSchema } from './primitives';

// 8-char minimum is the project floor; bcrypt rejects passwords over 72 bytes silently
// so we enforce that as a hard ceiling for clear error messaging.
export const SenhaSchema = z
  .string()
  .min(8, 'Senha mínimo 8 caracteres')
  .max(72, 'Senha máximo 72 caracteres');

export const RegisterEmpresaSchema = z.object({
  razaoSocial: z.string().min(2).max(120),
  nomeFantasia: z.preprocess(
    (v) => (typeof v === 'string' && v.trim() === '' ? undefined : v),
    z.string().trim().min(2).max(120).optional(),
  ),
  cnpj: CNPJSchema,
  responsavel: z.string().min(2).max(120),
  telefone: PhoneBRSchema,
  email: EmailSchema,
  senha: SenhaSchema,
  aceiteTermos: z.literal(true, { message: 'Aceite obrigatório' }),
  aceitePrivacidade: z.literal(true, { message: 'Aceite obrigatório' }),
});

export const RegisterProfissionalSchema = z.object({
  nomeCompleto: z.string().min(2).max(120),
  cpf: CPFSchema,
  cnpjMei: OptionalCNPJSchema,
  telefone: PhoneBRSchema,
  email: EmailSchema,
  senha: SenhaSchema,
  funcoes: z
    .array(z.enum(['MONTADOR', 'TECNICO', 'CARREGADOR']))
    .min(1, 'Selecione ao menos 1 função')
    .max(3, 'Máximo 3 funções'),
  aceiteTermos: z.literal(true, { message: 'Aceite obrigatório' }),
  aceitePrivacidade: z.literal(true, { message: 'Aceite obrigatório' }),
});

export const LoginSchema = z.object({
  email: EmailSchema,
  senha: z.string().min(1),
});

export const ForgotPasswordSchema = z.object({
  email: EmailSchema,
});

export const ResetPasswordSchema = z.object({
  email: EmailSchema,
  token: z.string().min(40),
  novaSenha: SenhaSchema,
});

export const UserSchema = z.object({
  id: z.string(),
  email: z.string(),
  // Phase 16 v1.3 — SOCIAL-01/02: `role` is `null` for OAuth-only users in
  // `pendente_completar` state (between OAuth callback and complete-signup).
  role: z.enum(['EMPRESA', 'PROFISSIONAL']).nullable(),
  empresaId: z.string().nullable().optional(),
  profissionalId: z.string().nullable().optional(),
});

export const TokenResponseSchema = z.object({
  accessToken: z.string(),
  user: UserSchema,
  // Capacitor-only fields; absent on PWA where refresh lives in HttpOnly cookie.
  refreshToken: z.string().optional(),
  refreshExpiresAt: z.string().optional(),
});

export const MeResponseSchema = UserSchema.extend({
  funcoes: z.array(z.enum(['MONTADOR', 'TECNICO', 'CARREGADOR'])).optional(),
  razaoSocial: z.string().optional(),
  nomeCompleto: z.string().optional(),
  /** Phase 11 (MEDIA-02): profissional avatar canonical URL; absent for empresa role. */
  avatarUrl: z.string().url().nullable().optional(),
  /** Phase 11 (MEDIA-03): empresa logo canonical URL; absent for profissional role. */
  logoUrl: z.string().url().nullable().optional(),
  /**
   * Phase 18 (KYC-02): founder admin flag. Read from `User.isAdmin` in the DB.
   * Frontend gates `/admin/kyc` routes on this signal. Server still re-checks
   * via DB-backed AdminGuard on every admin request (instant revocation).
   * Optional+default-false to keep existing /me consumers backwards-compatible.
   */
  isAdmin: z.boolean().optional(),
  /**
   * Phase 18 (KYC-03): mirrors `Profissional.verificado`. Set true after the
   * founder approves a KYC submissão; drives the verified-badge on perfil
   * cards. Absent for empresa role; optional default-false for transitional
   * compatibility with pre-Phase-18 /me payloads.
   */
  verificado: z.boolean().optional(),
  /**
   * Phase 20 v1.4 (LINK-01): list of OAuth providers attached to this user
   * (subset of {`google`,`apple`}). Empty array for email+senha-only users.
   * Drives the `/configuracoes/contas-conectadas` "Conectado"/"Conectar" UI.
   * Optional for transitional compatibility with pre-Phase-20 /me payloads.
   */
  oauthProviders: z.array(z.enum(['google', 'apple'])).optional(),
  /** Active empresa role for empresa members; null for non-empresa or legacy profiles. */
  empresaRole: z.enum(['ADMINISTRADOR', 'COLABORADOR', 'VISUALIZADOR']).nullable().optional(),
});

// ============================================================
// PHASE 16 v1.3 — SOCIAL-01/02: OAuth + complete-signup
// ============================================================

// Lowercase matches Prisma enum + passport `provider` field on profile payloads.
export const OAuthProviderSchema = z.enum(['google', 'apple']);
export type OAuthProvider = z.infer<typeof OAuthProviderSchema>;

// Empresa-branch fields. Mirror RegisterEmpresaSchema minus email/senha/aceites
// (consent + email already gathered during OAuth handshake; password absent for
// OAuth users entirely).
export const CompleteSignupEmpresaSchema = z.object({
  role: z.literal('empresa'),
  razaoSocial: z.string().min(2).max(120),
  nomeFantasia: z.preprocess(
    (v) => (typeof v === 'string' && v.trim() === '' ? undefined : v),
    z.string().trim().min(2).max(120).optional(),
  ),
  cnpj: CNPJSchema,
  responsavel: z.string().min(2).max(120),
  telefone: PhoneBRSchema,
});

// Profissional-branch fields. Mirror RegisterProfissionalSchema minus email/
// senha/aceites for the same reasons.
export const CompleteSignupProfissionalSchema = z.object({
  role: z.literal('profissional'),
  nomeCompleto: z.string().min(2).max(120),
  cpf: CPFSchema,
  cnpjMei: OptionalCNPJSchema,
  telefone: PhoneBRSchema,
  funcoes: z
    .array(z.enum(['MONTADOR', 'TECNICO', 'CARREGADOR']))
    .min(1, 'Selecione ao menos 1 função')
    .max(3, 'Máximo 3 funções'),
});

// Discriminated union — `role` is the discriminator. Zod 4 syntax.
export const CompleteSignupSchema = z.discriminatedUnion('role', [
  CompleteSignupEmpresaSchema,
  CompleteSignupProfissionalSchema,
]);
export type CompleteSignupInput = z.infer<typeof CompleteSignupSchema>;
export type CompleteSignupEmpresaInput = z.infer<typeof CompleteSignupEmpresaSchema>;
export type CompleteSignupProfissionalInput = z.infer<typeof CompleteSignupProfissionalSchema>;

export type RegisterEmpresaInput = z.infer<typeof RegisterEmpresaSchema>;
export type RegisterProfissionalInput = z.infer<typeof RegisterProfissionalSchema>;
export type LoginInput = z.infer<typeof LoginSchema>;
export type TokenResponse = z.infer<typeof TokenResponseSchema>;
export type User = z.infer<typeof UserSchema>;
export type MeResponse = z.infer<typeof MeResponseSchema>;
export type ResetPasswordInput = z.infer<typeof ResetPasswordSchema>;
export type ForgotPasswordInput = z.infer<typeof ForgotPasswordSchema>;

// ============================================================
// PHASE 20 v1.4 — LINK-01 / APPLE-01: account linking + native Apple
// ============================================================

/**
 * POST /auth/link-provider body. Connects a new OAuth identity (Google/Apple)
 * to the *currently authenticated* user. The idToken is verified server-side
 * (Google → tokeninfo endpoint; Apple → JWK + RS256). See AuthController.
 *
 * idToken length floor of 20 chars rejects empty strings / accidental nulls;
 * real Google/Apple idTokens are ≥ 800 chars.
 */
export const LinkProviderSchema = z.object({
  provider: OAuthProviderSchema,
  idToken: z.string().min(20, 'idToken obrigatório'),
});
export type LinkProviderInput = z.infer<typeof LinkProviderSchema>;

/**
 * POST /auth/apple/native-callback body. Sent by Capacitor
 * `@capacitor-community/apple-sign-in` after Sign in with Apple completes on
 * iOS. `fullName` + `email` are returned ONLY on the first authorize; later
 * sign-ins on the same device receive idToken only. Both optional — server
 * extracts sub (+ email when present) from the verified JWT regardless.
 */
export const AppleNativeCallbackSchema = z.object({
  idToken: z.string().min(20, 'idToken obrigatório'),
  fullName: z.string().min(1).max(120).optional(),
  email: EmailSchema.optional(),
});
export type AppleNativeCallbackInput = z.infer<typeof AppleNativeCallbackSchema>;

/**
 * Phase 46 v1.11 — APPLE-NATIVE-01. POST /auth/apple/native body.
 *
 * Same semantics as AppleNativeCallbackSchema but follows the field naming the
 * v1.11 spec calls out (`identityToken` mirrors the Apple plugin's response
 * key — see `ASAuthorizationAppleIDCredential.identityToken`). The legacy
 * `AppleNativeCallbackSchema` remains for the older `/auth/apple/native-callback`
 * route so existing clients keep working during rollout. Both routes share the
 * same JWKS validator + OAuthService.upsertFromApple plumbing.
 *
 * `authorizationCode` is forwarded by the plugin; Apple expects it back if you
 * want to exchange it for refresh tokens server-side (we do not — JWKS verify
 * + sub claim is sufficient for sign-in). Persisted as a no-op pass-through
 * so future server-side token exchange has the value already.
 */
export const AppleNativeAuthSchema = z.object({
  identityToken: z.string().min(20, 'identityToken obrigatório'),
  authorizationCode: z.string().min(1).max(2048).optional(),
  fullName: z.string().min(1).max(120).optional(),
});
export type AppleNativeAuthInput = z.infer<typeof AppleNativeAuthSchema>;

// ============================================================
// PHASE 21 v1.4 — LINK-01 web closure
// ============================================================

/**
 * POST /auth/oauth/link-init body. JWT-gated: the currently-authenticated
 * user requests an OAuth web-link start URL. Server mints a 5-minute state
 * JWT (`{ link: true, userId, nonce }`) signed with JWT_ACCESS_SECRET and
 * returns the absolute URL of `/auth/{provider}?state=<jwt>` for the mobile
 * client to redirect to. The state carries the userId so the OAuth callback
 * knows which user to attach the new identity to.
 */
export const OAuthLinkInitInputSchema = z.object({
  provider: OAuthProviderSchema,
});
export type OAuthLinkInitInput = z.infer<typeof OAuthLinkInitInputSchema>;

/**
 * POST /auth/oauth/link-init response. `url` is an absolute URL the mobile
 * client immediately redirects to via `window.location.href = url`. The
 * state JWT is embedded as a query param; the strategy verifies it on the
 * callback and branches into link mode when `state.link === true`.
 */
export const OAuthLinkInitResponseSchema = z.object({
  url: z.string().url(),
  state: z.string().min(10),
});
export type OAuthLinkInitResponse = z.infer<typeof OAuthLinkInitResponseSchema>;
